import { Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { FC, ReactElement } from 'react';

import { hasValue } from '@core/utils/equality';

import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner';
import type { Column } from '../TableDataModel';
import { DataTableRow } from './DataTableRow';

type MessageTableBodyProps = {
  message: string | ReactElement;
  height?: string;
  colSpan: number;
};

const MessageTableBody: FC<MessageTableBodyProps> = ({ message, height = '15rem', colSpan }) => (
  <TableBody>
    <TableRow>
      <TableCell sx={{ height, textAlign: 'center' }} colSpan={colSpan}>
        {typeof message === 'string' ? <Typography variant='h5'>{message}</Typography> : message}
      </TableCell>
    </TableRow>
  </TableBody>
);

type DataTableBodyProps<TRow extends { id: string }> = {
  isLoading: boolean;
  isFetched: boolean;
  isSuccess: boolean;
  pageRows: TRow[] | undefined;
  emptyRows: number;
  columnCount: number;
  renderedColumns: ReadonlySet<Column<TRow>>;
  expanded: string[];
  onIsExpandedChange: (id: string, newIsExpanded: boolean) => void;
  selectedRowIDs: readonly string[];
  includeSelect: boolean;
  onEditPress?: (value: TRow) => void;
  canEditRow: (value: TRow) => boolean;
  onArchivePress?: (row: TRow) => void;
  isRowArchived?: (row: TRow) => boolean;
  canArchive?: boolean;
  renderStickyActions?: (row: TRow) => ReactElement;
  getRowHighlight?: (row: TRow) => string | undefined;
  renderRowDetails?: (value: TRow) => ReactElement;
  stickyRowDetails?: boolean;
  nested?: boolean;
  onSelectClick: (rowUuid: string) => void;
  highlightRowDetails?: boolean;
};

export const DataTableBody = <TRow extends { id: string }>({
  isLoading,
  isFetched,
  isSuccess,
  pageRows,
  emptyRows,
  columnCount,
  renderedColumns,
  expanded,
  onIsExpandedChange,
  selectedRowIDs,
  includeSelect,
  onEditPress,
  canEditRow,
  onArchivePress,
  isRowArchived,
  canArchive,
  renderStickyActions,
  getRowHighlight,
  renderRowDetails,
  stickyRowDetails,
  nested,
  onSelectClick,
  highlightRowDetails,
}: DataTableBodyProps<TRow>): ReactElement => {
  const colSpan = 2 * columnCount;

  if (isLoading && !isFetched) {
    const heightInRows = Math.max(8, emptyRows + (pageRows ?? []).length);
    return (
      <MessageTableBody
        message={<LoadingSpinner />}
        height={`${2.5 * heightInRows}rem`}
        colSpan={colSpan}
      />
    );
  }

  if (!isSuccess || pageRows === undefined) {
    // Looking at isSuccess to detect errors instead of isError, because isError is false when react-query
    // retries the request if the window loses and regains focus.
    return <MessageTableBody message='Failed to load this page of the table' colSpan={colSpan} />;
  }

  if (renderedColumns.size <= 0) {
    return <MessageTableBody message='No columns selected' colSpan={colSpan} />;
  }

  if (pageRows.length === 0) {
    return <MessageTableBody message='No rows' colSpan={colSpan} />;
  }

  return (
    <TableBody>
      {pageRows.map((row, index) => {
        const isItemSelected = selectedRowIDs.includes(row.id);
        const isExpanded = expanded.includes(row.id) && hasValue(renderRowDetails);

        return (
          <DataTableRow
            key={row.id}
            row={row}
            index={index}
            isSelected={isItemSelected}
            isExpanded={isExpanded}
            getRowHighlight={getRowHighlight}
            includeSelect={includeSelect}
            selectedRowIDs={selectedRowIDs}
            onSelectClick={onSelectClick}
            onEditPress={onEditPress}
            canEditRow={canEditRow}
            onArchivePress={onArchivePress}
            isRowArchived={isRowArchived}
            canArchive={canArchive}
            renderStickyActions={renderStickyActions}
            renderedColumns={renderedColumns}
            onIsExpandedChange={onIsExpandedChange}
            highlightRowDetails={highlightRowDetails}
            renderRowDetails={renderRowDetails}
            stickyRowDetails={stickyRowDetails}
            nested={nested}
          />
        );
      })}
      {emptyRows > 0 && (
        <TableRow>
          <TableCell sx={{ height: `${2.5 * emptyRows}rem` }} colSpan={colSpan} />
        </TableRow>
      )}
    </TableBody>
  );
};
